import React, { useState } from 'react';
import { useLms } from '../../context/LmsContext';
import { Card, DataTable, PageHeader, StatusBadge } from '../../components/ui';
import { byId, fullName, getCourse, getOwner } from '../../utils/lmsSelectors';
import { downloadCertificatePdf } from '../../utils/certificatePdf';

function CertificateModal({ certificate, data, onClose }) {
  if (!certificate) return null;
  const course = getCourse(data, certificate.courseId);
  const learner = byId(data.users, certificate.userId);
  const issuer = fullName(getOwner(data, course));
  const issued = new Date(certificate.issuedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const assignment = data.assignments.find((item) => item.id === certificate.assignmentId);
  const completed = assignment?.completedAt
    ? new Date(assignment.completedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : issued;

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div className="builder-modal certificate-modal" role="dialog" aria-modal="true" aria-label="Certificate preview" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Certificate {certificate.certificateNumber}</h2>
            <p className="muted small">Preview certificate before downloading.</p>
          </div>
          <button className="button button-ghost" type="button" onClick={onClose}>Close</button>
        </div>
        <div className="certificate-preview">
          <p className="certificate-small-heading">Certificate of Completion</p>
          <h1>{fullName(learner)}</h1>
          <p>has successfully completed the internal training course</p>
          <h2>{course?.title || 'Untitled course'}</h2>
          <p>Completed on {completed}</p>
          <div className="certificate-footer-grid">
            <div>
              <span className="signature-line" />
              <strong>Authorized Issuer</strong>
              <p>{issuer}</p>
            </div>
            <div>
              <strong>Certificate No.</strong>
              <p>{certificate.certificateNumber}</p>
              <strong>Issued</strong>
              <p>{issued}</p>
            </div>
          </div>
        </div>
        <div className="button-row modal-actions-row">
          <button className="button button-primary" type="button" disabled={certificate.status !== 'active'} onClick={() => downloadCertificatePdf(certificate, data)}>Download certificate</button>
          <button className="button button-ghost" type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function LearnerCertificates() {
  const { data, currentUser } = useLms();
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const rows = data.certificates.filter((cert) => cert.userId === currentUser.id).map((cert) => ({
    ...cert,
    course: getCourse(data, cert.courseId)?.title,
    issued: new Date(cert.issuedAt).toLocaleDateString()
  }));
  return (
    <>
      <PageHeader title="My certificates" description="View and download active course completion certificates generated after all requirements are met." />
      <Card>
        <DataTable
          rows={rows}
          columns={[
            { key: 'certificateNumber', label: 'Certificate #', render: (row) => <button className="link-button" type="button" onClick={() => setSelectedCertificate(row)}>{row.certificateNumber}</button> },
            { key: 'course', label: 'Course' },
            { key: 'issued', label: 'Issued' },
            { key: 'status', label: 'Status', render: (row) => <StatusBadge status={row.status} /> },
            { key: 'download', label: 'Download', render: (row) => <button className="button button-secondary" disabled={row.status !== 'active'} onClick={() => downloadCertificatePdf(row, data)}>Download PDF</button> }
          ]}
          emptyMessage="No certificates yet. Complete a certificate-enabled course first."
        />
      </Card>
      <CertificateModal certificate={selectedCertificate} data={data} onClose={() => setSelectedCertificate(null)} />
    </>
  );
}
